// =====================================================================
//  CASCARÓN — login, sesión, barra lateral y ruteo
//
//  Mantiene la estructura del prototipo: #login a pantalla completa, y
//  después #app con la grilla de 236px de barra lateral más el contenido.
//
//  El ruteo es por hash (#/listado) a propósito: la aplicación se sirve
//  estática desde remates2026.consa.cl y así no hace falta ningún
//  .htaccess que reescriba las rutas hacia index.html.
// =====================================================================

import { useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes, useSearchParams } from 'react-router-dom'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'
import { usePerfil } from './datos/consultas'
import { Cargando } from './componentes/ui'
import { Panel } from './vistas/Panel'
import { Listado } from './vistas/Listado'
import { Lotes } from './vistas/Lotes'
import { Presupuesto } from './vistas/Presupuesto'
import { Bitacora } from './vistas/Bitacora'
import { Cronograma } from './vistas/Cronograma'
import { Causa } from './vistas/Causa'
import { Pesos } from './vistas/Pesos'
import { Admin } from './vistas/Admin'
import { Ficha } from './vistas/Ficha'
import type { RolUsuario } from './tipos/dominio'

const ETIQUETA_ROL: Record<RolUsuario, string> = {
  MASTER: 'Máster',
  ANALISTA: 'Analista',
  LECTOR: 'Lector',
}

// La barra lateral. `master: true` esconde la entrada para los demás roles
// —lo mismo que hacía `body.master` en el CSS del prototipo—, pero acá es
// solo comodidad: el permiso de verdad lo aplica RLS en el motor.
//
// Falta el importador de nóminas TGR, que es el paso 11. No se lista
// porque un enlace que no lleva a nada es peor que no tener el enlace.
const VISTAS: Array<{ grupo?: string; ruta?: string; titulo?: string; master?: boolean }> = [
  { grupo: 'Operación' },
  { ruta: '/panel', titulo: 'Panel de control' },
  { ruta: '/listado', titulo: 'Listado de concesiones' },
  { ruta: '/lotes', titulo: 'Lotes de subasta' },
  { ruta: '/presupuesto', titulo: 'Presupuesto y posturas' },
  { grupo: 'Proceso' },
  { ruta: '/cronograma', titulo: 'Cronograma legal' },
  { ruta: '/causa', titulo: 'Causa judicial' },
  { grupo: 'Configuración' },
  { ruta: '/pesos', titulo: 'Pesos del puntaje', master: true },
  { ruta: '/admin', titulo: 'Comunas y usuarios', master: true },
  { ruta: '/bitacora', titulo: 'Bitácora' },
]

export function App() {
  const [sesion, setSesion] = useState<Session | null>(null)
  const [cargandoSesion, setCargandoSesion] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSesion(data.session)
      setCargandoSesion(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, s) => setSesion(s))
    return () => sub.subscription.unsubscribe()
  }, [])

  if (cargandoSesion) return <PantallaCarga />
  if (!sesion) return <Login />
  return <Autorizado authId={sesion.user.id} correo={sesion.user.email ?? ''} />
}

function PantallaCarga() {
  return (
    <div id="login">
      <div className="lbox">
        <div className="logo">CS</div>
        <h1>Portal de Remate Minero</h1>
        <p className="sub">Verificando la sesión…</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------
//  Login
// ---------------------------------------------------------------------

function Login() {
  const [entrando, setEntrando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const entrar = async () => {
    setEntrando(true)
    setError(null)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + window.location.pathname },
    })
    if (error) {
      setError(error.message)
      setEntrando(false)
    }
  }

  return (
    <div id="login">
      <div className="lbox">
        <div className="logo">CS</div>
        <h1>Portal de Remate Minero</h1>
        <p className="sub">
          Seguimiento de concesiones con patentes impagas · Región de Tarapacá · Proceso 2026
        </p>

        <button className="gbtn" onClick={entrar} disabled={entrando}>
          <IconoGoogle />
          {entrando ? 'Abriendo Google…' : 'Ingresar con Google'}
        </button>

        {error ? (
          <div className="alert a-bad" style={{ marginTop: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="hint">
          <b style={{ color: 'var(--txt2)' }}>Acceso restringido.</b> Solo entran los correos
          registrados por el máster. Un correo de Google válido que no esté en la lista no obtiene
          acceso a ningún dato. Si tu correo debería estar y no entra, solicita a{' '}
          <code style={{ color: 'var(--acc2)' }}>consorciosalinero@gmail.com</code> que lo agregue.
        </div>
      </div>
    </div>
  )
}

function IconoGoogle() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.1 24.5c0-1.6-.1-2.8-.4-4.1H24v8.4h12.5c-.3 2.1-1.6 5.2-4.6 7.3l7.6 5.9c4.5-4.2 6.6-10.3 6.6-17.5z"
      />
      <path
        fill="#FBBC05"
        d="M10.4 28.7A14.6 14.6 0 019.6 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 000 24c0 3.9.9 7.5 2.6 10.8l7.8-6.1z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.5 0 11.9-2.1 15.9-5.9l-7.6-5.9c-2 1.4-4.8 2.4-8.3 2.4-6.4 0-11.7-3.7-13.6-9.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------
//  Sesión válida — se comprueba que el correo esté autorizado
// ---------------------------------------------------------------------

function Autorizado({ authId, correo }: { authId: string; correo: string }) {
  const { data: perfil, isLoading, isError, error } = usePerfil(authId)

  // Deja constancia del ingreso. El trigger de allowlist solo corre en el
  // PRIMER ingreso (INSERT en auth.users), así que los siguientes se
  // registran desde acá.
  useEffect(() => {
    if (perfil) void supabase.rpc('app_registrar_acceso')
  }, [perfil])

  if (isLoading) return <PantallaCarga />

  // Un correo que no está en `usuario` no ve ninguna fila: RLS se lo
  // niega todo. Eso es lo que significa perfil === null.
  if (isError || !perfil) {
    return <NoAutorizado correo={correo} detalle={isError ? String(error) : null} />
  }

  return (
    <>
      <div id="app" className={perfil.rol === 'MASTER' ? 'master' : undefined}>
        <aside>
          <div className="brand">
            <div className="logo">CS</div>
            <div>
              <b>Remate Minero</b>
              <span>Tarapacá · 2026</span>
            </div>
          </div>

          <nav>
            {VISTAS.filter((v) => !v.master || perfil.rol === 'MASTER').map((v) =>
              v.grupo ? (
                <div className="grp" key={v.grupo}>
                  {v.grupo}
                </div>
              ) : (
                <NavLink
                  key={v.ruta}
                  to={v.ruta!}
                  className={({ isActive }) => (isActive ? 'on' : '')}
                >
                  {v.titulo}
                </NavLink>
              ),
            )}
          </nav>

          <div className="who">
            <b>{perfil.email}</b>
            <span className="rol">{ETIQUETA_ROL[perfil.rol]}</span>
            <button onClick={() => void supabase.auth.signOut()}>Cerrar sesión</button>
          </div>
        </aside>

        <main>
          <Routes>
            <Route path="/panel" element={<Panel />} />
            <Route path="/listado" element={<Listado />} />
            <Route path="/lotes" element={<Lotes rol={perfil.rol} />} />
            <Route path="/presupuesto" element={<Presupuesto rol={perfil.rol} />} />
            <Route path="/cronograma" element={<Cronograma rol={perfil.rol} />} />
            <Route path="/causa" element={<Causa rol={perfil.rol} />} />
            <Route path="/bitacora" element={<Bitacora />} />
            {/* Estas dos redirigen si el rol no corresponde. Es cortesía: RLS
                ya impide escribir, pero mostrar una pantalla de administración
                que no hace nada es peor que no mostrarla. */}
            <Route
              path="/pesos"
              element={perfil.rol === 'MASTER' ? <Pesos /> : <Navigate to="/panel" replace />}
            />
            <Route
              path="/admin"
              element={perfil.rol === 'MASTER' ? <Admin /> : <Navigate to="/panel" replace />}
            />
            <Route path="*" element={<Navigate to="/panel" replace />} />
          </Routes>
        </main>
      </div>

      <FichaAbierta rol={perfil.rol} />
    </>
  )
}

/**
 * La ficha es un cajón sobre la vista actual, igual que en el prototipo.
 * Va en el parámetro `?ficha=<rol nacional>` en vez de una ruta anidada
 * para que se pueda abrir desde cualquier vista y el enlace sea
 * compartible.
 */
function FichaAbierta({ rol }: { rol: RolUsuario }) {
  const [params, setParams] = useSearchParams()
  const rolNacional = params.get('ficha')
  if (!rolNacional) return null

  const cerrar = () => {
    const siguiente = new URLSearchParams(params)
    siguiente.delete('ficha')
    setParams(siguiente, { replace: true })
  }

  return <Ficha rolNacional={rolNacional} rolUsuario={rol} cerrar={cerrar} />
}

function NoAutorizado({ correo, detalle }: { correo: string; detalle: string | null }) {
  return (
    <div id="login">
      <div className="lbox">
        <div className="logo">CS</div>
        <h1>Tu correo no está autorizado</h1>
        <p className="sub">
          Google confirmó <b style={{ color: 'var(--txt)' }}>{correo}</b>, pero ese correo no está
          registrado en el portal.
        </p>
        <div className="alert a-warn" style={{ marginTop: 4 }}>
          El acceso no se otorga por tener una cuenta de Google válida: el máster tiene que
          registrar el correo primero. Solicita a{' '}
          <code style={{ color: 'var(--acc2)' }}>consorciosalinero@gmail.com</code> que te agregue y
          vuelve a entrar.
        </div>
        {detalle ? (
          <div className="hint" style={{ wordBreak: 'break-word' }}>
            {detalle}
          </div>
        ) : null}
        <button className="btn" style={{ marginTop: 18 }} onClick={() => void supabase.auth.signOut()}>
          Salir e intentar con otro correo
        </button>
      </div>
    </div>
  )
}

/** Cabecera de página, igual que h2.page + p.pagesub del prototipo. */
export function Pagina({ titulo, sub }: { titulo: string; sub: React.ReactNode }) {
  return (
    <>
      <h2 className="page">{titulo}</h2>
      <p className="pagesub">{sub}</p>
    </>
  )
}

export { Cargando }
